/**
 * The apothecary's carved front (lifted from lantern-water's
 * `apothecary-exterior.js`): carved eave brackets, lintels with a paired
 * sprig in relief and a brass bud, and a green roundel with a mortar emblem on
 * both faces, hung from a projecting iron support. Built at (x, front) in
 * `parent`'s space; every finish is a shared town material.
 */
import * as THREE from 'three';
import type { Ctx } from './kit.ts';

export const apothecaryExterior = (ctx: Ctx, parent: THREE.Object3D, x: number, front: number, width: number, height: number): THREE.Group => {
  const m = ctx.m;
  const box = (...a: Parameters<Ctx['box']>): ReturnType<Ctx['box']> => ctx.box(...a);
  const mesh = (...a: Parameters<Ctx['mesh']>): ReturnType<Ctx['mesh']> => ctx.mesh(...a);
  const line = (...a: Parameters<Ctx['line']>): ReturnType<Ctx['line']> => ctx.line(...a);
  const p = new THREE.Group();
  p.name = 'apothecary-carved-exterior';
  p.position.set(x, 0, front);
  parent.add(p);
  const profile = new THREE.Shape();
  profile.moveTo(0, 0);
  profile.lineTo(0.52, 0);
  profile.lineTo(0.52, -0.1);
  profile.bezierCurveTo(0.38, -0.07, 0.31, -0.2, 0.43, -0.28);
  profile.bezierCurveTo(0.52, -0.4, 0.33, -0.48, 0.26, -0.34);
  profile.bezierCurveTo(0.17, -0.3, 0.14, -0.57, 0.06, -0.61);
  profile.lineTo(0, -0.61);
  profile.closePath();
  const bracket = new THREE.ExtrudeGeometry(profile, { depth: 0.13, bevelEnabled: true, bevelSize: 0.012, bevelThickness: 0.012, bevelSegments: 1, curveSegments: 5 });
  bracket.rotateY(-Math.PI / 2);
  for (const dx of [-0.44, -0.16, 0.16, 0.44]) {
    mesh(p, bracket, m.wood, dx * width + 0.065, height - 0.04, 0.11);
    box(p, dx * width, height - 0.04, 0.38, 0.22, 0.11, 0.59, m.darkWood);
  }
  const leaf = new THREE.Shape();
  leaf.moveTo(0, 0);
  leaf.quadraticCurveTo(-0.09, 0.07, 0, 0.21);
  leaf.quadraticCurveTo(0.075, 0.1, 0, 0);
  const leafGeo = new THREE.ExtrudeGeometry(leaf, { depth: 0.018, bevelEnabled: false, curveSegments: 4 });
  for (const dx of [-width * 0.29, width * 0.29]) {
    const yy = height * 0.73 + 0.76;
    box(p, dx, yy, 0.12, 1.5, 0.24, 0.14, m.darkWood);
    box(p, dx, yy + 0.15, 0.17, 1.62, 0.065, 0.24, m.wood);
    for (const side of [-1, 1]) {
      line(p, [dx + side * 0.07, yy - 0.025, 0.207], [dx + side * 0.58, yy + 0.005, 0.207], 0.012, m.wood);
      for (let i = 0; i < 3; i += 1) {
        const stemX = dx + side * (0.18 + i * 0.15);
        for (const up of [-1, 1]) {
          const o = mesh(p, leafGeo, m.wood, stemX, yy - 0.02, 0.211, 0.65, 0.65, 1);
          o.rotation.z = up > 0 ? -side * 0.65 : Math.PI + side * 0.65;
        }
      }
    }
    const bud = mesh(p, new THREE.OctahedronGeometry(0.065), m.brass, dx, yy - 0.015, 0.23);
    bud.scale.set(1, 1.2, 0.35);
  }
  const sx = width * 0.49;
  const sy = height * 0.73 + 0.08;
  const sz = 1.05;
  const top = sy + 0.73;
  box(p, sx, top - 0.07, 0.12, 0.15, 0.45, 0.08, m.iron);
  line(p, [sx, top, 0.1], [sx, top, sz + 0.07], 0.04, m.iron);
  line(p, [sx, top - 0.25, 0.15], [sx, top, sz * 0.82], 0.025, m.iron);
  line(p, [sx - 0.23, top, sz], [sx + 0.23, top, sz], 0.028, m.iron);
  for (const side of [-1, 1]) line(p, [sx + side * 0.18, top, sz], [sx + side * 0.18, sy + 0.32, sz], 0.017, m.iron);
  const roundel = mesh(p, new THREE.CylinderGeometry(0.4, 0.4, 0.075, 24), m.teal, sx, sy, sz);
  roundel.rotation.x = Math.PI / 2;
  for (const side of [-1, 1]) {
    mesh(p, new THREE.TorusGeometry(0.365, 0.018, 5, 32), m.brass, sx, sy, sz + side * 0.044);
    const emblem = new THREE.Group();
    emblem.position.set(sx, sy, sz + side * 0.043);
    if (side < 0) emblem.rotation.y = Math.PI;
    p.add(emblem);
    const bowl = new THREE.Shape();
    bowl.moveTo(-0.235, 0.055);
    bowl.lineTo(0.235, 0.055);
    bowl.quadraticCurveTo(0.2, -0.145, 0.075, -0.165);
    bowl.lineTo(-0.075, -0.165);
    bowl.quadraticCurveTo(-0.2, -0.145, -0.235, 0.055);
    mesh(emblem, new THREE.ExtrudeGeometry(bowl, { depth: 0.014, bevelEnabled: false, curveSegments: 8 }), m.cream, 0, 0, 0.006);
    box(emblem, 0, 0.06, 0.025, 0.5, 0.045, 0.035, m.brass);
    box(emblem, 0, -0.185, 0.016, 0.23, 0.035, 0.028, m.cream);
    line(emblem, [0.03, 0.09, 0.03], [0.16, 0.255, 0.03], 0.051, m.cream);
  }
  return p;
};
